/**
 * Chart Formatting Utilities
 *
 * Formatting functions for chart tooltips, axis labels, and values.
 */

/**
 * Format large currency values with appropriate suffix
 * @param {number} value - The value to format
 * @param {string} prefix - Currency prefix (default '$')
 * @returns {string} Formatted string like "$310B" or "$1.5M"
 */
export const formatCurrency = (value, prefix = '$') => {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e12) {
    return `${sign}${prefix}${(absValue / 1e12).toFixed(1)}T`;
  }

  if (absValue >= 1e9) {
    return `${sign}${prefix}${(absValue / 1e9).toFixed(0)}B`;
  }

  if (absValue >= 1e6) {
    return `${sign}${prefix}${(absValue / 1e6).toFixed(1)}M`;
  }

  if (absValue >= 1e3) {
    return `${sign}${prefix}${(absValue / 1e3).toFixed(0)}K`;
  }

  return `${sign}${prefix}${absValue.toFixed(0)}`;
};

/**
 * Format decimal as percentage
 * @param {number} value - Decimal value (e.g., 0.38) or whole number (e.g., 38)
 * @param {number} decimals - Decimal places
 * @param {boolean} isDecimal - Whether value is decimal (0.38) or whole (38)
 * @returns {string} Formatted percentage like "38%"
 */
export const formatPercentage = (value, decimals = 0, isDecimal = true) => {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  const percentage = isDecimal ? value * 100 : value;

  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format ROI multiplier
 * @param {number} value - ROI value (e.g., 7.5)
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted ROI like "7.5x"
 */
export const formatMultiplier = (value, decimals = 1) => {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${value.toFixed(decimals)}x`;
};

/**
 * Format a number with commas
 * @param {number} value - The value to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted number like "1,234,567"
 */
export const formatNumber = (value, decimals = 0) => {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format a value out of a maximum (e.g., "7/10")
 * @param {number} value - The value
 * @param {number} max - Maximum value
 * @returns {string} Formatted string like "7/10"
 */
export const formatOutOf = (value, max = 10) => {
  if (value == null || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${value}/${max}`;
};

/**
 * Identity formatter - returns value as-is
 * @param {*} value - Any value
 * @returns {*} Same value
 */
export const identity = (value) => value;
